/**
 * A number, and — on hover or keyboard focus — the definition behind it.
 *
 * "Every number shown has a metrics.definitions row (name, sql, window, owner) and the UI shows
 * the definition on hover" is a Global Constraint, not a nicety. A dashboard number whose
 * definition nobody can see is how two people end up arguing about whose spreadsheet is right.
 *
 * The definition is exposed twice, on purpose:
 *
 *   - as a permanently-rendered sr-only node that `aria-describedby` points at, so a screen
 *     reader gets it without hovering anything. Pointing aria-describedby at the tooltip itself
 *     is the classic version of this bug: the id only exists while the tooltip is open, so for
 *     most of the tile's life the attribute dangles and the number has no definition at all.
 *   - as the hover/focus tooltip, which adds the SQL in a monospace block a person can read.
 *
 * Author: Danish Khan
 */
import * as Tooltip from '@radix-ui/react-tooltip';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import type { MetricValue } from '../lib/types.ts';
import { formatMetric, temporalLabel } from '../lib/format.ts';

export interface MetricTileProps {
  metric: MetricValue;
  /** For metrics where up is bad (unanswered, cost), so the delta colour is not backwards. */
  lowerIsBetter?: boolean;
  onClick?: () => void;
}

export function MetricTile({ metric, lowerIsBetter = false, onClick }: MetricTileProps): ReactNode {
  const { definition, value, delta, unavailable } = metric;
  const good = delta === null || delta === 0 ? null : lowerIsBetter ? delta < 0 : delta > 0;
  const descId = `metric-def-${definition.name}`;

  return (
    <Tooltip.Root delayDuration={120}>
      <Tooltip.Trigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-describedby={descId}
          className={clsx(
            'h1-card flex min-w-[9.5rem] flex-1 cursor-help flex-col items-start gap-1 px-3.5 py-3 text-left',
            'transition-colors hover:border-line-strong',
          )}
          data-testid={`metric-${definition.name}`}
        >
          <span className="text-[11px] font-medium tracking-wide text-fg-muted uppercase">
            {definition.label}
          </span>
          <span
            className={clsx('text-2xl font-semibold tabular-nums', value === null ? 'text-fg-subtle' : 'text-fg')}
            title={value === null ? 'Nothing in this window to measure — not a zero' : undefined}
          >
            {formatMetric(value, definition.unit)}
          </span>
          <span className="flex items-center gap-1.5 text-[11px] text-fg-subtle">
            <span>{value === null ? `${definition.window} · nothing to measure` : definition.window}</span>
            {delta !== null && delta !== 0 && (
              <span className={clsx('font-medium', good ? 'text-ok' : 'text-danger')}>
                {delta > 0 ? '▲' : '▼'} {formatMetric(Math.abs(delta), definition.unit)}
              </span>
            )}
          </span>
          {/*
            Why there is no number, in the API's own sentence — the same rule the Analytics tiles
            follow. `evaluateMetrics` fills `unavailable` from the metric's `nullMeans` whenever the
            value is null ("No handoff opened in the last seven days has been claimed … Not 0 ms: a
            queue nobody touched is not a fast queue"). This tile used to discard it and print an em
            dash, so on a tenant with almost no data every tile on Health was a dash while the same
            metrics on Home explained themselves. Not a tooltip: somebody reading a screenshot has
            to see it, or they will fill the gap in with a zero.
          */}
          {unavailable !== null && (
            <span
              className="text-[11px] leading-relaxed whitespace-normal text-fg-muted"
              data-testid={`metric-unavailable-${definition.name}`}
            >
              {unavailable}
            </span>
          )}
        </button>
      </Tooltip.Trigger>

      <span id={descId} className="sr-only" data-testid={`metric-definition-${definition.name}`}>
        {definition.label}. Metric {definition.name}, window {definition.window},{' '}
        {temporalLabel(definition.temporal)}, owned by {definition.owner}. Defined as: {definition.sql}
      </span>

      <Tooltip.Portal>
        <Tooltip.Content
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={12}
          className="z-50 max-w-sm rounded-[var(--h1-radius)] border border-line bg-surface p-3 text-left shadow-lg"
        >
          <p className="text-xs font-semibold text-fg">{definition.label}</p>
          <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]">
            <dt className="text-fg-subtle">metric</dt>
            <dd className="font-mono text-fg-muted">{definition.name}</dd>
            <dt className="text-fg-subtle">window</dt>
            <dd className="text-fg-muted">{definition.window}</dd>
            {/* The tense, for the reason Analytics prints it: "last 30 days" and "right now" are
                different questions, and a reader who cannot tell which one a figure answers is a
                reader who will quote the wrong one. */}
            <dt className="text-fg-subtle">tense</dt>
            <dd className="text-fg-muted">{temporalLabel(definition.temporal)}</dd>
            <dt className="text-fg-subtle">owner</dt>
            <dd className="text-fg-muted">{definition.owner}</dd>
          </dl>
          <pre className="mt-2 max-h-40 overflow-auto rounded-md bg-surface-2 p-2 font-mono text-[10px] leading-relaxed whitespace-pre-wrap text-fg-muted">
            {definition.sql}
          </pre>
          <Tooltip.Arrow className="fill-[var(--h1-border)]" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}
